import { useEffect, useState } from 'react';
import { getConfig, saveConfig } from '../api';
import type { Session } from '../types';

type CallMode = 'forward' | 'ai';
type PhoneCfg = {
  phoneNumber?: string;
  ownerPhone?: string;
  numberPortingEnabled?: boolean;
  callMode?: CallMode;
  forwardRings?: number;
  forwardTo?: string;
  screenCalls?: boolean;
  playGreeting?: boolean;
  missedCallGreeting?: string;
};

const DEFAULT_GREETING = 'Sorry we missed your call. We are sending you a text right now.';

function pretty(e164?: string) {
  const d = (e164 ?? '').replace(/\D/g, '').slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : e164 ?? '';
}

export function PhoneTab({ session }: { session: Session }) {
  const [cfg, setCfg] = useState<PhoneCfg | null>(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void getConfig(session)
      .then((c) => {
        const x = c as unknown as PhoneCfg;
        setCfg({
          ...x,
          callMode: x.callMode ?? (x.numberPortingEnabled ? 'forward' : 'ai'),
          forwardRings: x.forwardRings ?? 3,
          forwardTo: pretty(x.forwardTo ?? x.ownerPhone),
          screenCalls: x.screenCalls ?? false,
          playGreeting: x.playGreeting ?? false,
          missedCallGreeting: x.missedCallGreeting ?? DEFAULT_GREETING,
        });
      })
      .catch((e) => setErr(String(e)));
  }, [session]);

  if (!cfg) return <div className="empty">{err || 'Loading phone settings…'}</div>;

  const set = (p: Partial<PhoneCfg>) => {
    setCfg({ ...cfg, ...p });
    setMsg('');
  };
  const forward = cfg.callMode === 'forward';
  const rings = cfg.forwardRings ?? 3;

  return (
    <>
      <div className="topbar">
        <div>
          <h1>Phone</h1>
          <div className="sub">What happens when someone calls {pretty(cfg.phoneNumber)}. Saves to this shop’s config. No deploy.</div>
        </div>
      </div>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          setMsg('');
          setErr('');
          setSaving(true);
          try {
            const patch = {
              callMode: cfg.callMode,
              forwardRings: rings,
              forwardTo: cfg.forwardTo ?? '',
              screenCalls: !!cfg.screenCalls,
              playGreeting: !!cfg.playGreeting,
              missedCallGreeting: cfg.missedCallGreeting ?? '',
            };
            await saveConfig(session, patch as unknown as Parameters<typeof saveConfig>[1]);
            setMsg('Saved. The next call uses this.');
          } catch (ex) {
            setErr(ex instanceof Error ? ex.message : 'Save failed');
          } finally {
            setSaving(false);
          }
        }}
      >
        <label>
          When someone calls
          <select value={cfg.callMode} onChange={(e) => set({ callMode: e.target.value as CallMode })}>
            <option value="forward">Ring my phone first. Text them if I miss it.</option>
            <option value="ai">Don’t ring me. Text them right away.</option>
          </select>
        </label>

        {forward && (
          <>
            <label>
              Ring this phone (US or Canada)
              <input type="tel" value={cfg.forwardTo ?? ''} placeholder="(248) 555-0123" onChange={(e) => set({ forwardTo: e.target.value })} />
            </label>
            <label>
              How long to ring
              <select value={rings} onChange={(e) => set({ forwardRings: Number(e.target.value) })}>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <option key={n} value={n}>
                    {n} {n === 1 ? 'ring' : 'rings'} (about {n * 5} seconds)
                  </option>
                ))}
              </select>
            </label>
            <label>
              Press-1 screening
              <select value={cfg.screenCalls ? 'on' : 'off'} onChange={(e) => set({ screenCalls: e.target.value === 'on' })}>
                <option value="off">Off. Picking up connects the call.</option>
                <option value="on">On. I press 1 to take the call.</option>
              </select>
            </label>
            <p className="sub">
              With screening on, you hear “New customer call, press 1” when you pick up. If you don’t press 1, or your
              voicemail answers, the caller gets the text instead of your voicemail.
            </p>
          </>
        )}

        <label>
          Spoken message before the call ends
          <select value={cfg.playGreeting ? 'on' : 'off'} onChange={(e) => set({ playGreeting: e.target.value === 'on' })}>
            <option value="off">None. Just end the call. The text still goes out.</option>
            <option value="on">Play a short message</option>
          </select>
        </label>
        {cfg.playGreeting && (
          <label>
            Message ({(cfg.missedCallGreeting ?? '').length}/300)
            <textarea rows={3} maxLength={300} value={cfg.missedCallGreeting ?? ''} onChange={(e) => set({ missedCallGreeting: e.target.value })} />
          </label>
        )}
        {cfg.playGreeting && forward && (
          <p className="sub">Heads up: with “Ring my phone first,” the message may not play yet. It works with “Text them right away.”</p>
        )}

        {msg && <p>{msg}</p>}
        {err && <p className="err">{err}</p>}
        <button className="primary" type="submit" disabled={saving}>
          {saving ? 'Saving…' : 'Save phone settings'}
        </button>
      </form>
    </>
  );
}
